// =============================================================================
// src/app/export-explore-csv.ts — Exportacao CSV real do dataset da Explorar.
//
// Proveniencia: reforma institucional 2026-06 (Fase 4). O botao "Exportar
//   dados" da topbar era um toast morto ("Exportando dados…") — afordance
//   institucional nao pode ser stub. Agora gera um CSV REAL dos 62 municipios
//   do EXPLORE_DATASET (mesma fonte do mapa) e dispara o download via Blob.
// Convencao pt-BR: separador ';' (Excel pt-BR) e decimais com virgula.
// =============================================================================

import { EXPLORE_DATASET } from '../data';
import { ELECTION_OFFICE, OFFICE_LABEL } from '../features/explore/constants';
import type { ElectoralYear } from './types';

/** Ano exportado (ultimo pleito municipal — mesmo default da Explorar). */
const EXPORT_YEAR: ElectoralYear = '2024';

/** Cabecalho do CSV (separador ';' por convencao Excel pt-BR). */
const CSV_HEADER = 'Município;Região;População (mil);Eleitorado (mil);1º colocado;% 1º;2º colocado';

/** Linha CSV de um municipio (decimais com virgula; campo vazio quando sem dado). */
function exploreCsvRow(name: string, region: string, habK: number, eleitoresK: number, p: string, pct: number, s: string): string {
  const num = (value: number): string => String(value).replace('.', ',');
  return `${name};${region};${num(habK)};${num(eleitoresK)};${p};${num(pct)};${s}`;
}

/**
 * Monta o CSV completo (header + 62 municipios) do ano exportado.
 *
 * @example
 *   buildExploreCsv().split('\n').length // 63 (header + 62 municipios)
 */
export function buildExploreCsv(): string {
  const rows = Object.values(EXPLORE_DATASET.munis).map((mun) => {
    /* v8 ignore next -- fallback exigido por noUncheckedIndexedAccess; serie el completa no dataset real */
    const result = mun.el[EXPORT_YEAR] ?? { p: '—', pct: 0, s: '—' };
    return exploreCsvRow(mun.name, mun.region, mun.habK, mun.eleitoresK, result.p, result.pct, result.s);
  });
  return [CSV_HEADER, ...rows].join('\n');
}

/** Nome do arquivo baixado (ano + cargo do pleito, ex.: prefeito-2024). */
function exportFileName(): string {
  return `mapa-politico-am-${OFFICE_LABEL[ELECTION_OFFICE[EXPORT_YEAR]].toLowerCase()}-${EXPORT_YEAR}.csv`;
}

/**
 * Gera o Blob e dispara o download do CSV. Devolve o nº de municipios
 * exportados (usado no toast de confirmacao da topbar).
 *
 * @example
 *   const count = downloadExploreCsv(); // 62; arquivo .csv baixado
 */
export function downloadExploreCsv(): number {
  const csv = buildExploreCsv();
  // BOM UTF-8: Excel pt-BR abre acentos corretamente sem importacao manual.
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  triggerBrowserDownload(url, exportFileName());
  URL.revokeObjectURL(url);
  return Object.keys(EXPLORE_DATASET.munis).length;
}

/** Cria um <a download> efemero e clica (padrao Blob-download sem deps). */
function triggerBrowserDownload(url: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}
